import { useCallback, useEffect, useRef } from "react";

// No interval: measure monotonic elapsed time at save/visibility boundaries.
export default function useAnswerDuration(initialSeconds = null) {
  const elapsedMs = useRef(Math.max(0, initialSeconds || 0) * 1000);
  const startedAt = useRef(null);
  const settle = useCallback(() => {
    if (startedAt.current !== null) {
      elapsedMs.current += Math.max(0, performance.now() - startedAt.current);
      startedAt.current = null;
    }
  }, []);
  const getSeconds = useCallback(() => {
    const currentMs = startedAt.current === null
      ? 0 : Math.max(0, performance.now() - startedAt.current);
    return Math.floor((elapsedMs.current + currentMs) / 1000);
  }, []);
  const resumeFrom = useCallback((seconds) => {
    // Restoring an autosave already made in this session must not double-count.
    settle();
    elapsedMs.current = Math.max(elapsedMs.current, (seconds || 0) * 1000);
    if (document.visibilityState === "visible") {
      startedAt.current = performance.now();
    }
  }, [settle]);
  useEffect(() => {
    const visibilityChanged = () => {
      settle();
      if (document.visibilityState === "visible") {
        startedAt.current = performance.now();
      }
    };
    visibilityChanged();
    document.addEventListener("visibilitychange", visibilityChanged);
    window.addEventListener("pagehide", settle);
    window.addEventListener("pageshow", visibilityChanged);
    return () => {
      settle();
      document.removeEventListener("visibilitychange", visibilityChanged);
      window.removeEventListener("pagehide", settle);
      window.removeEventListener("pageshow", visibilityChanged);
    };
  }, [settle]);
  return { getSeconds, resumeFrom };
}
